// F6 region traversal (CONTRACT "Keyboard model"): top bar, rail, list,
// workspace, inspector. Regions that are not rendered (list collapsed,
// inspector closed, rail hidden under 1024) are skipped. The active region
// gets data-region-active for the 1 px inset border and its first focusable
// element receives focus.
export const REGION_ORDER = ['topbar', 'rail', 'list', 'work', 'inspector'] as const;
export type Region = (typeof REGION_ORDER)[number];

// What the narrow-width inspector sheet paints over. The sheet is fixed from
// the bottom of the top bar to the top of the status bar and is full width at
// 767 and below, so the tape and the workspace are behind it and nothing else
// is. Passed to BottomSheet, which makes them inert while it is open so Tab
// cannot focus a control the reader cannot see (WCAG 2.2 AA 2.4.11).
export const SHEET_COVERED_REGIONS: readonly string[] = ['.tape', '[data-region="work"]'];

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isRendered(element: HTMLElement): boolean {
  return element.getClientRects().length > 0;
}

export function visibleRegions(root: ParentNode): ReadonlyArray<{ readonly region: Region; readonly element: HTMLElement }> {
  return REGION_ORDER.flatMap((region) => {
    const element = root.querySelector<HTMLElement>(`[data-region="${region}"]`);
    return element && isRendered(element) ? [{ region, element }] : [];
  });
}

export function nextRegion(current: Region | null, step: 1 | -1, regions: ReadonlyArray<Region>): Region | null {
  if (regions.length === 0) return null;
  const index = current === null ? -1 : regions.indexOf(current);
  if (index === -1) return step === 1 ? (regions[0] ?? null) : (regions[regions.length - 1] ?? null);
  return regions[(index + step + regions.length) % regions.length] ?? null;
}

// The first focusable element that is actually painted. The top bar's menu
// button is display:none above 1023 px, and calling focus() on an unrendered
// element is a no-op, which left F6 marking a region without moving focus
// into it (redo-1 P1).
export function firstFocusable(region: HTMLElement, except?: Element | null): HTMLElement | null {
  for (const candidate of region.querySelectorAll<HTMLElement>(FOCUSABLE)) {
    if (candidate !== except && isRendered(candidate)) return candidate;
  }
  return null;
}

// Escape out of a text field. Blurring and stopping leaves
// document.activeElement on <body>, so the next Tab restarts at the top of a
// 248 stop document. Focus moves to the region the field belongs to instead,
// and the tab order continues from where the reader already was.
export function leaveTextField(active: HTMLElement): void {
  const region = active.closest<HTMLElement>('[data-region]');
  active.blur();
  if (!region) return;
  // Never the field itself: it is the first focusable of its own region on
  // any screen where nothing precedes it, and refocusing it would undo Escape.
  (firstFocusable(region, active) ?? region).focus();
}

export function focusRegion(root: ParentNode, region: Region): void {
  for (const candidate of root.querySelectorAll<HTMLElement>('[data-region]')) {
    candidate.dataset['regionActive'] = String(candidate.dataset['region'] === region);
  }
  const element = root.querySelector<HTMLElement>(`[data-region="${region}"]`);
  if (!element) return;
  (firstFocusable(element) ?? element).focus();
}
